import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import api, { mediaUrl, formatPrice, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import { toast } from "sonner";

export default function Checkout() {
    const [params] = useSearchParams();
    const mode = params.get("mode");
    const couponParam = params.get("coupon") || "";
    const { items, direct, clear, clearDirect } = useCart();
    const { user } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();

    const sourceItems = mode === "buynow" && direct ? [direct] : items;

    const [form, setForm] = useState({
        name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
        line: "", city: "", state: "", pincode: "",
    });
    const [coupon, setCoupon] = useState(couponParam);
    const [totals, setTotals] = useState(null);
    const [method, setMethod] = useState("online");
    const [placing, setPlacing] = useState(false);

    useEffect(() => {
        if (sourceItems.length === 0) return;
        const payload = { items: sourceItems.map((i) => ({ product_id: i.product_id, qty: i.qty })), coupon_code: coupon || null };
        api.post("/cart/validate", payload).then(({ data }) => setTotals(data));
    }, [JSON.stringify(sourceItems), coupon]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email, phone: f.phone || user.phone }));
    }, [user]);

    if (sourceItems.length === 0) {
        return <div className="py-28 text-center"><p className="font-serif text-3xl">Nothing to checkout</p><Link to="/shop" className="text-gold mt-4 inline-block">Go to Shop</Link></div>;
    }

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const placeOrder = async () => {
        if (!totals) return;
        setPlacing(true);
        const body = {
            items: sourceItems.map((i) => ({ product_id: i.product_id, qty: i.qty })),
            customer: { name: form.name, phone: form.phone, email: form.email },
            address: { line: form.line, city: form.city, state: form.state, pincode: form.pincode },
            coupon_code: coupon || null,
            payment_method: method,
        };
        try {
            const { data } = await api.post("/orders", body);
            if (data.cod) {
                afterSuccess(data.order_number);
                return;
            }
            openRazorpay(data);
        } catch (err) {
            toast.error(apiError(err));
            setPlacing(false);
        }
    };

    const afterSuccess = (orderNumber) => {
        if (mode === "buynow") clearDirect();
        else clear();
        navigate(`/order/${orderNumber}`);
    };

    const openRazorpay = (data) => {
        if (!window.Razorpay) {
            toast.error("Payment library failed to load. Please refresh.");
            setPlacing(false);
            return;
        }
        const options = {
            key: data.razorpay_key_id,
            amount: data.amount,
            currency: data.currency,
            name: "ELVOSH",
            description: "Order Payment",
            order_id: data.razorpay_order_id,
            prefill: data.prefill,
            theme: { color: "#C8A97E" },
            handler: async (resp) => {
                try {
                    await api.post("/orders/verify", {
                        order_number: data.order_number,
                        razorpay_order_id: resp.razorpay_order_id,
                        razorpay_payment_id: resp.razorpay_payment_id,
                        razorpay_signature: resp.razorpay_signature,
                    });
                    toast.success("Payment successful!");
                    afterSuccess(data.order_number);
                } catch (err) {
                    toast.error("Payment verification failed. If money was deducted, contact support with your order ID.");
                    navigate(`/order/${data.order_number}`);
                }
            },
            modal: { ondismiss: () => { setPlacing(false); toast.info("Payment cancelled"); } },
        };
        const rz = new window.Razorpay(options);
        rz.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPlacing(false); });
        rz.open();
    };

    return (
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-10 md:py-14">
            <h1 className="font-serif text-4xl tracking-tight mb-10">Checkout</h1>
            <div className="grid lg:grid-cols-5 gap-10">
                <div className="lg:col-span-3 space-y-6">
                    <div>
                        <h2 className="eyebrow mb-4">Contact Details</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Full Name" testid="checkout-name" value={form.name} onChange={set("name")} />
                            <Field label="Phone Number" testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
                            <div className="sm:col-span-2"><Field label="Email" testid="checkout-email" type="email" value={form.email} onChange={set("email")} /></div>
                        </div>
                    </div>
                    <div>
                        <h2 className="eyebrow mb-4">Delivery Address</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2"><Field label="Complete Address" testid="checkout-line" value={form.line} onChange={set("line")} placeholder="House no, street, area" /></div>
                            <Field label="City" testid="checkout-city" value={form.city} onChange={set("city")} />
                            <Field label="State" testid="checkout-state" value={form.state} onChange={set("state")} />
                            <Field label="PIN Code" testid="checkout-pincode" value={form.pincode} onChange={set("pincode")} placeholder="6-digit" />
                        </div>
                    </div>
                    <div>
                        <h2 className="eyebrow mb-4">Payment Method</h2>
                        <div className="space-y-3">
                            <label className="flex items-center gap-3 border border-[#E8DFD5] p-4 cursor-pointer bg-white has-[:checked]:border-gold">
                                <input type="radio" name="pay" checked={method === "online"} onChange={() => setMethod("online")} data-testid="pay-online" className="accent-gold" />
                                <span className="text-sm">Pay Online — UPI, Cards, Net Banking <span className="text-muted-foreground">(Secured by Razorpay)</span></span>
                            </label>
                            {settings.cod_enabled && (
                                <label className="flex items-center gap-3 border border-[#E8DFD5] p-4 cursor-pointer bg-white has-[:checked]:border-gold">
                                    <input type="radio" name="pay" checked={method === "cod"} onChange={() => setMethod("cod")} data-testid="pay-cod" className="accent-gold" />
                                    <span className="text-sm">Cash on Delivery</span>
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-cream-soft p-6 sticky top-24">
                        <h2 className="font-serif text-xl mb-5">Your Order</h2>
                        <div className="space-y-4 max-h-64 overflow-y-auto mb-4">
                            {(totals?.items || sourceItems.map((i) => ({ ...i.snap, qty: i.qty }))).map((it, idx) => (
                                <div key={idx} className="flex gap-3">
                                    <div className="h-16 w-14 bg-white overflow-hidden shrink-0">
                                        {it.image ? <img src={mediaUrl(it.image)} alt="" className="w-full h-full object-cover" /> : <ProductPlaceholder className="w-full h-full" label="" />}
                                    </div>
                                    <div className="flex-1 text-sm">
                                        <p className="line-clamp-1">{it.name}</p>
                                        <p className="text-muted-foreground">Qty {it.qty}</p>
                                    </div>
                                    <span className="text-sm">{formatPrice((it.price || 0) * it.qty)}</span>
                                </div>
                            ))}
                        </div>
                        {totals && (
                            <div className="space-y-2.5 text-sm border-t border-[#E8DFD5] pt-4">
                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal (MRP)</span><span>{formatPrice(totals.subtotal_mrp)}</span></div>
                                <div className="flex justify-between text-gold"><span>Product Discount</span><span>− {formatPrice(totals.product_discount)}</span></div>
                                {totals.coupon_discount > 0 && <div className="flex justify-between text-gold"><span>Coupon ({totals.coupon_code})</span><span>− {formatPrice(totals.coupon_discount)}</span></div>}
                                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{totals.shipping_charge ? formatPrice(totals.shipping_charge) : "FREE"}</span></div>
                                <div className="flex justify-between font-semibold text-base border-t border-[#E8DFD5] pt-3"><span>Total Payable</span><span data-testid="checkout-total">{formatPrice(totals.total)}</span></div>
                            </div>
                        )}
                        <button data-testid="place-order-btn" onClick={placeOrder} disabled={placing} className="w-full mt-6 bg-gold text-white py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            <Lock className="h-4 w-4" /> {placing ? "Processing…" : method === "cod" ? "Place Order" : "Pay Securely"}
                        </button>
                        <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mt-3"><ShieldCheck className="h-3.5 w-3.5" /> Secure server-side verified payment</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, testid, value, onChange, type = "text", placeholder }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
            <input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full mt-1.5 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold" />
        </div>
    );
}
